import { useEffect, DependencyList } from 'react';

/**
 * Runs `onBlur` whenever the window loses focus (e.g. switching tabs/apps, or focus moving into
 * an embedded iframe — a click inside an iframe never bubbles to this document, but it does blur
 * this window, which is the only way to detect it here).
 *
 * `deps` controls when the listener is re-subscribed, same as a raw `useEffect` dependency array.
 * Defaults to `[]` (subscribe once) — pass the values `onBlur` actually closes over (e.g. `[value]`)
 * only when it closes over something that isn't already stable (a plain state setter, for example,
 * never needs this — it's the same reference for the component's whole lifetime).
 */
export default function useOnWindowBlur(onBlur: () => void, deps: DependencyList = []): void {
    useEffect(() => {
        window.addEventListener('blur', onBlur);
        return () => window.removeEventListener('blur', onBlur);
    }, deps);
}

/**
 * Runs `onFocusAway` when focus moves into an iframe embedded on the page — the only way to
 * detect a click inside one, since it fires in the iframe's own document and never bubbles here.
 *
 * `deps` controls when the listener is re-subscribed, same as `useOnWindowBlur` — pass the values
 * `onFocusAway` closes over (e.g. `[value]`) when it isn't already stable.
 */
export function useOnIframeFocusAway(onFocusAway: () => void, deps: DependencyList = []): void {
    useOnWindowBlur(() => {
        setTimeout(() => {
            if (document.activeElement?.tagName === 'IFRAME') onFocusAway();
        }, 0);
    }, deps);
}
